/* eslint-disable react/no-unknown-property -- Firebolt потребляет css-проп при сборке. */

// LIVE VOICE HEALTH & CONSENTED AUTO-GAIN — диагностика голоса и согласие.
// Пропы: { world, onClose }. ESC закрывает экран.
//
// Договор экрана, ради которого он существует: МЫ НИКОГДА НЕ МЕНЯЕМ ГОЛОС САМИ.
// Экран измеряет, показывает и ПРЕДЛАГАЕТ. Любое изменение применяется только
// нажатием игрока — либо автоматикой, которую он сам заранее включил (opt-in),
// и её всегда можно откатить кнопкой ВЕРНУТЬ КАК БЫЛО.
//
// Метрики — настоящие: уровень, пик, шумовой порог и счётчик перегруза берутся
// с анализатора реального тракта, а уровни собеседников — из LiveKit.

import { css } from '@firebolt-dev/css'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  MicIcon,
  MicOffIcon,
  PlayIcon,
  SaveIcon,
  PowerIcon,
  ShieldCheckIcon,
  RotateCcwIcon,
  UsersIcon,
  WifiIcon,
  InfoIcon,
  CheckIcon,
  LockIcon,
  HeartIcon,
  Volume2Icon,
  AlertTriangleIcon,
  XIcon,
} from 'lucide-react'

import { COLORS, RADII, space, MOTION, SHADOWS, TYPE, ICON_SIZES } from '../theme'
import { voicePeers, setPeerVolume, worldStatus } from '../screen-data'
import { saveCustomPreset, formatParamValue, VOICE_PARAMS } from '../voice-chain'
import { getVoiceService } from '../voice-service'
import {
  VoiceHealthMonitor,
  suggestionFor,
  applyChanges,
  verdictFor,
  dbToScale,
  ZONES,
  CONSENT_MODES,
  loadConsent,
  saveConsent,
  rememberDevice,
  WIZARD_STEPS,
  evaluateWizardStep,
} from '../voice-health'
import { ScreenShell, IdentityBadge, MetaStat, Panel, Btn, Toggle, RadioRow, LevelBar, Stat, ActionBar, Chip } from './VoiceKit'

const TONE_COLOR = {
  success: COLORS.success,
  warning: COLORS.warning,
  error: COLORS.error,
  muted: COLORS.textSecondary,
}

export function VoiceHealthScreen({ world, onClose }) {
  // Тракт общий на весь клиент — экран им пользуется, но не владеет.
  const service = useMemo(() => getVoiceService(), [])
  // «как было» на момент открытия — для кнопки ВЕРНУТЬ КАК БЫЛО
  const originalRef = useRef({ settings: { ...service.settings }, enabled: service.isPublishingEnabled() })

  const [settings, setSettings] = useState(service.settings)
  const [enabled, setEnabled] = useState(service.isPublishingEnabled())
  const [consent, setConsent] = useState(() => loadConsent())
  const [snapshot, setSnapshot] = useState(null)
  const [peers, setPeers] = useState(() => ({ available: false, connected: false, peers: [] }))
  const [status, setStatus] = useState(() => worldStatus(world))
  const [micError, setMicError] = useState(null)
  const [ready, setReady] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [mutedAll, setMutedAll] = useState(false)
  const [saved, setSaved] = useState(null)
  const [autoLog, setAutoLog] = useState(null)

  // мастер проверки микрофона
  const [wizardStep, setWizardStep] = useState(-1)
  const [wizardResults, setWizardResults] = useState({})
  const [wizardCountdown, setWizardCountdown] = useState(0)

  const monitorRef = useRef(new VoiceHealthMonitor())
  const rafRef = useRef(0)
  const autoAppliedAt = useRef(0)

  // --- Берём общий тракт на время жизни экрана ---
  useEffect(() => {
    let cancelled = false
    service
      .acquire('voice-health')
      .then(() => {
        if (!cancelled) setReady(true)
      })
      .catch(() => {
        if (!cancelled) setMicError(service.error || 'Микрофон недоступен.')
      })
    return () => {
      cancelled = true
      // прослушивание себя — режим экрана, гасим его обязательно
      service.chain.setMonitor(false)
      service.release('voice-health')
    }
  }, [service])

  // --- Тик метрик ---
  useEffect(() => {
    if (!ready) return undefined
    const tick = () => {
      const chain = service.chain
      if (chain?.ready) {
        setSnapshot(monitorRef.current.tick(chain.levels(enabled ? 'enhanced' : 'original'), performance.now()))
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [ready, enabled, service])

  // --- Участники голосового канала ---
  useEffect(() => {
    const update = () => {
      setPeers(voicePeers(world))
      setStatus(worldStatus(world))
    }
    update()
    const timer = setInterval(update, 400)
    return () => clearInterval(timer)
  }, [world])

  // --- ESC ---
  useEffect(() => {
    const onKey = event => {
      if (event.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const suggestion = useMemo(() => {
    if (consent.mode === 'warn') return null
    const item = suggestionFor(snapshot, settings)
    if (!item) return null
    if (consent.dismissed?.[item.id]) return null
    return item
  }, [snapshot, settings, consent])

  // Предупреждение для режима «только предупреждать»
  const warning = useMemo(() => {
    if (consent.mode !== 'warn' || !snapshot?.settled) return null
    const v = snapshot.verdict
    return v.id === 'good' || v.id === 'silent' ? null : v
  }, [consent.mode, snapshot])

  const applySettings = useCallback(
    next => {
      setSettings(service.update(next))
      setSaved(null)
    },
    [service]
  )

  // --- Автоматика: только если игрок сам её включил ---
  useEffect(() => {
    if (consent.mode !== 'auto' || !suggestion) return
    // не чаще раза в 6 секунд, иначе усиление «поедет»
    if (performance.now() - autoAppliedAt.current < 6000) return
    autoAppliedAt.current = performance.now()
    const next = applyChanges(settings, suggestion.changes)
    applySettings(next)
    setAutoLog(`Автоматика применила: ${suggestion.changes.map(c => `${c.label} ${c.display}`).join(', ')}`)
    setTimeout(() => setAutoLog(null), 6000)
  }, [consent.mode, suggestion, settings, applySettings])

  const handleApply = useCallback(
    item => {
      const next = applyChanges(settings, item.changes)
      applySettings(next)
      if (consent.perDeviceMemory) rememberDevice(service.deviceId, next)
      setPreviewing(false)
      service.chain.setMonitor(false)
    },
    [settings, applySettings, consent.perDeviceMemory, service]
  )

  // Локальная проба: слышим себя, наружу ничего не уходит
  const handlePreview = useCallback(
    item => {
      const chain = service.chain
      if (!chain?.ready) return
      if (previewing) {
        chain.setMonitor(false)
        // возвращаем тракт к сохранённым значениям — проба ничего не меняет
        chain.update(settings)
        setPreviewing(false)
        return
      }
      if (item) chain.update(applyChanges(settings, item.changes))
      chain.setMonitor(true)
      setPreviewing(true)
    },
    [previewing, settings, service]
  )

  const handleDismiss = useCallback(
    (item, forever) => {
      if (forever) {
        const next = { ...consent, dismissed: { ...consent.dismissed, [item.id]: true } }
        setConsent(next)
        saveConsent(next)
      }
      setPreviewing(false)
      service.chain.setMonitor(false)
      service.chain.update(settings)
    },
    [consent, settings, service]
  )

  const handleRestore = useCallback(() => {
    const base = originalRef.current
    setSettings(service.update(base.settings))
    setEnabled(service.setEnabled(base.enabled))
    service.setPublishingConsent(base.enabled)
    service.chain.setMonitor(false)
    setPreviewing(false)
    monitorRef.current.reset()
    setSaved('Вернули как было при открытии экрана')
    setTimeout(() => setSaved(null), 3000)
  }, [service])

  const updateConsent = useCallback(patch => {
    setConsent(prev => {
      const next = { ...prev, ...patch }
      saveConsent(next)
      return next
    })
  }, [])

  const toggleMuteAll = useCallback(() => {
    setMutedAll(prev => {
      const next = !prev
      peers.peers.filter(p => !p.isMe).forEach(p => setPeerVolume(world, p.id, next ? 0 : 1))
      return next
    })
  }, [peers, world])

  // --- Мастер проверки ---
  const runWizardStep = useCallback(
    index => {
      const step = WIZARD_STEPS[index]
      if (!step) return
      setWizardStep(index)
      if (!step.seconds) {
        const result = evaluateWizardStep(step.id, { chain: service.chain, snapshot })
        setWizardResults(prev => ({ ...prev, [step.id]: result }))
        return
      }
      // шагам с измерением даём накопить данные
      if (step.id === 'noise') monitorRef.current.reset()
      setWizardCountdown(step.seconds)
      const timer = setInterval(() => {
        setWizardCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer)
            const result = evaluateWizardStep(step.id, { chain: service.chain, snapshot: monitorRef.current.snapshot() })
            setWizardResults(r => ({ ...r, [step.id]: result }))
            return 0
          }
          return prev - 1
        })
      }, 1000)
    },
    [snapshot, service]
  )

  const handleSave = useCallback(() => {
    const ok = service.save()
    if (consent.perDeviceMemory) rememberDevice(service.deviceId, settings)
    saveConsent(consent)
    setSaved(ok ? 'Настройки сохранены' : 'Браузер не дал сохранить (приватный режим)')
    setTimeout(() => setSaved(null), 3000)
  }, [settings, service, consent])

  const verdict = snapshot?.verdict || verdictFor({ rmsDb: -100, peakDb: -100 })
  const voicePeerList = peers.peers || []

  return (
    <ScreenShell
      label='WORLD'
      title='LIVE VOICE HEALTH & CONSENTED AUTO-GAIN'
      subtitle='Твой голос. Твой выбор. Всегда сохраняем как есть.'
      ariaLabel='Здоровье голоса'
      onClose={onClose}
      meta={
        <>
          <IdentityBadge title='ORIGINAL VOICE PRESERVED' subtitle='Мы никогда не меняем голос сами' />
          <MetaStat
            icon={<WifiIcon size={ICON_SIZES.sm} color={peers.connected ? COLORS.success : COLORS.textSecondary} />}
            label='ГОЛОС'
            value={peers.connected ? status.place : 'не подключён'}
            tone={peers.connected ? 'success' : undefined}
          />
          <MetaStat
            icon={<UsersIcon size={ICON_SIZES.sm} color={COLORS.textSecondary} />}
            label='В МИРЕ'
            value={String(status.online)}
          />
        </>
      }
    >
      <div
        css={css`
          flex: 1 1 auto;
          min-height: 0;
          display: grid;
          grid-template-columns: 360px 1fr 400px;
          gap: ${space(2)};
          padding: ${space(2)};

          @media (max-width: 1400px) {
            grid-template-columns: 300px 1fr 340px;
          }
          @media (max-width: 1100px) {
            grid-template-columns: 1fr;
            grid-auto-rows: min-content;
            overflow-y: auto;
          }
        `}
      >
        {/* ── ЛЕВО: голосовой канал ───────────────────────────────────── */}
        <Panel
          title={`ГОЛОСОВОЙ КАНАЛ (${voicePeerList.length})`}
          info='Уровни берём у LiveKit — это то, что реально слышат остальные.'
        >
          {!peers.available ? (
            <EmptyNote text='Голосовая комната не поднята. Уровни собеседников появятся, когда подключится войс.' />
          ) : voicePeerList.length === 0 ? (
            <EmptyNote text='В канале пока никого.' />
          ) : (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${space(1)};
              `}
            >
              {voicePeerList.map(peer => (
                <PeerRow key={peer.id || peer.name} peer={peer} />
              ))}
            </div>
          )}

          {peers.available && voicePeerList.length > 1 && (
            <Btn full variant={mutedAll ? 'success' : 'secondary'} onClick={toggleMuteAll}>
              {mutedAll ? <Volume2Icon size={ICON_SIZES.xs} /> : <MicOffIcon size={ICON_SIZES.xs} />}
              {mutedAll ? 'ВЕРНУТЬ ЗВУК' : 'ЗАГЛУШИТЬ ВСЕХ'}
            </Btn>
          )}
        </Panel>

        {/* ── ЦЕНТР: здоровье голоса ──────────────────────────────────── */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${space(2)};
            min-height: 0;
            overflow-y: auto;
          `}
        >
          <Panel title='ТВОЙ ГОЛОС' info='Всё считается с анализатора живого тракта.'>
            {micError ? (
              <div
                css={css`
                  display: flex;
                  gap: ${space(1)};
                  padding: ${space(1)};
                  border: 1px solid ${COLORS.errorSoft};
                  border-radius: ${RADII.lg};
                  background: ${COLORS.errorSoft};
                  font-size: ${TYPE.caption.size};
                `}
              >
                <AlertTriangleIcon size={ICON_SIZES.sm} color={COLORS.error} />
                <span>{micError}</span>
              </div>
            ) : (
              <>
                <ZoneMeter snapshot={snapshot} verdict={verdict} />
                <div
                  css={css`
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: ${space(1)};
                    margin-top: ${space(2)};

                    @media (max-width: 600px) {
                      grid-template-columns: repeat(2, 1fr);
                    }
                  `}
                >
                  <Stat label='УРОВЕНЬ (RMS)' value={fmtDb(snapshot?.rmsDb)} />
                  <Stat label='ПИК' value={fmtDb(snapshot?.peakDb)} tone={snapshot?.peakDb >= ZONES.clipPeakDb ? 'error' : undefined} />
                  <Stat label='ШУМОВОЙ ПОРОГ' value={fmtDb(snapshot?.noiseFloorDb)} tone={snapshot?.noiseFloorDb > -45 ? 'warning' : undefined} />
                  <Stat
                    label='ПЕРЕГРУЗ'
                    value={String(snapshot?.clippingCount ?? 0)}
                    tone={snapshot?.clippingCount ? 'error' : 'success'}
                  />
                </div>
                <HistoryChart history={snapshot?.history || []} />
              </>
            )}
          </Panel>

          {suggestion && (
            <SuggestionCard
              suggestion={suggestion}
              previewing={previewing}
              testLocally={consent.testLocallyBeforeApplying}
              onPreview={() => handlePreview(suggestion)}
              onApply={() => handleApply(suggestion)}
              onDismiss={forever => handleDismiss(suggestion, forever)}
            />
          )}

          {warning && (
            <div
              css={css`
                display: flex;
                gap: ${space(1)};
                align-items: center;
                padding: ${space(1)} ${space(2)};
                border: 1px solid ${COLORS.warningSoft};
                border-radius: ${RADII.xl};
                background: ${COLORS.warningSoft};
                font-size: ${TYPE.caption.size};
              `}
            >
              <AlertTriangleIcon size={ICON_SIZES.sm} color={COLORS.warning} />
              <span>
                {warning.ru}. Ты выбрал режим «только предупреждать» — менять ничего не предлагаем.
              </span>
            </div>
          )}

          {autoLog && (
            <div
              css={css`
                display: flex;
                gap: ${space(1)};
                align-items: center;
                padding: ${space(1)} ${space(2)};
                border: 1px solid ${COLORS.borderStrong};
                border-radius: ${RADII.xl};
                background: ${COLORS.cyanSoft};
                font-size: ${TYPE.caption.size};
              `}
            >
              <InfoIcon size={ICON_SIZES.sm} color={COLORS.cyan} />
              <span>{autoLog} — можно откатить кнопкой «Вернуть как было».</span>
            </div>
          )}

          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              gap: ${space(1)};
              padding: ${space(2)};
              border: 1px solid ${COLORS.successSoft};
              border-radius: ${RADII.xl};
              background: ${COLORS.successSoft};
              text-align: center;
              font-size: ${TYPE.caption.size};
              color: ${COLORS.textSecondary};
            `}
          >
            <ShieldCheckIcon size={ICON_SIZES.md} color={COLORS.success} />
            <span>
              Мы никогда не меняем твой голос автоматически.
              <br />
              Решение всегда за тобой.
            </span>
          </div>
        </div>

        {/* ── ПРАВО: согласие и мастер ────────────────────────────────── */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${space(2)};
            min-height: 0;
            overflow-y: auto;
          `}
        >
          <Panel title='СОГЛАСИЕ И КОНТРОЛЬ' info='Как экрану вести себя, когда он видит проблему.'>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${space(0)};
              `}
            >
              {CONSENT_MODES.map(mode => (
                <RadioRow
                  key={mode.id}
                  checked={consent.mode === mode.id}
                  onChange={() => updateConsent({ mode: mode.id })}
                  label={mode.ru}
                  hint={mode.hintRu}
                  warn={mode.id === 'auto' ? 'Изменения всегда обратимы.' : null}
                  recommended={mode.recommended}
                />
              ))}
            </div>

            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${space(0)};
                margin-top: ${space(1)};
              `}
            >
              <Toggle
                checked={consent.testLocallyBeforeApplying}
                onChange={v => updateConsent({ testLocallyBeforeApplying: v })}
                label='СНАЧАЛА ПОСЛУШАТЬ СЕБЯ'
                hint='Проба идёт только в твои наушники — остальные её не слышат.'
                icon={<LockIcon size={ICON_SIZES.xs} />}
              />
              <Toggle
                checked={consent.perDeviceMemory}
                onChange={v => updateConsent({ perDeviceMemory: v })}
                label='ПАМЯТЬ ПО УСТРОЙСТВАМ'
                hint='У гарнитуры и встроенного микрофона свои настройки.'
                icon={<SaveIcon size={ICON_SIZES.xs} />}
              />
            </div>

            <div
              css={css`
                display: flex;
                align-items: center;
                gap: ${space(1)};
                margin-top: ${space(1)};
                padding: ${space(1)};
                border: 1px solid ${COLORS.border};
                border-radius: ${RADII.lg};
              `}
            >
              <RotateCcwIcon size={ICON_SIZES.sm} color={COLORS.textSecondary} />
              <span
                css={css`
                  flex: 1 1 auto;
                  font-size: ${TYPE.caption.size};
                  color: ${COLORS.textSecondary};
                `}
              >
                Вернуть всё к тому, как микрофон звучал при открытии экрана.
              </span>
              <Btn size='small' onClick={handleRestore}>
                ВЕРНУТЬ
              </Btn>
            </div>
          </Panel>

          <Panel
            title='ПРОВЕРКА МИКРОФОНА'
            info='Четыре шага, каждый — настоящее измерение.'
            right={
              <span
                css={css`
                  font-size: ${TYPE.caption.size};
                  color: ${COLORS.textSecondary};
                `}
              >
                {Object.keys(wizardResults).length} / {WIZARD_STEPS.length}
              </span>
            }
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${space(0)};
              `}
            >
              {WIZARD_STEPS.map((step, index) => {
                const result = wizardResults[step.id]
                const active = wizardStep === index
                return (
                  <div
                    key={step.id}
                    css={css`
                      display: flex;
                      align-items: flex-start;
                      gap: ${space(1)};
                      padding: ${space(0)};
                      border-radius: ${RADII.md};
                      background: ${active ? COLORS.cyanSoft : 'transparent'};
                    `}
                  >
                    <span
                      css={css`
                        margin-top: 2px;
                        color: ${result ? (result.ok ? COLORS.success : COLORS.warning) : COLORS.textSecondary};
                      `}
                    >
                      {result ? result.ok ? <CheckIcon size={ICON_SIZES.xs} /> : <AlertTriangleIcon size={ICON_SIZES.xs} /> : <LockIcon size={ICON_SIZES.xs} />}
                    </span>
                    <span
                      css={css`
                        flex: 1 1 auto;
                        min-width: 0;
                      `}
                    >
                      <span
                        css={css`
                          display: block;
                          font-size: 0.72rem;
                        `}
                      >
                        {step.ru}
                      </span>
                      {active && wizardCountdown > 0 && (
                        <span
                          css={css`
                            display: block;
                            font-size: ${TYPE.caption.size};
                            color: ${COLORS.cyan};
                          `}
                        >
                          измеряем… {wizardCountdown}
                        </span>
                      )}
                      {result && (
                        <span
                          css={css`
                            display: block;
                            font-size: ${TYPE.caption.size};
                            color: ${COLORS.textSecondary};
                          `}
                        >
                          {result.note}
                        </span>
                      )}
                    </span>
                  </div>
                )
              })}
            </div>

            <Btn
              full
              variant='primary'
              disabled={!ready || wizardCountdown > 0}
              onClick={() => {
                const nextIndex = wizardStep + 1 >= WIZARD_STEPS.length ? 0 : wizardStep + 1
                if (nextIndex === 0) setWizardResults({})
                runWizardStep(nextIndex)
              }}
            >
              <PlayIcon size={ICON_SIZES.xs} />
              {wizardStep < 0 ? 'НАЧАТЬ ПРОВЕРКУ' : wizardStep + 1 >= WIZARD_STEPS.length ? 'ПРОЙТИ ЗАНОВО' : 'СЛЕДУЮЩИЙ ШАГ'}
            </Btn>
          </Panel>
        </div>
      </div>

      <ActionBar
        left={
          <Btn
            variant={enabled ? 'destructive' : 'success'}
            onClick={() => {
              setEnabled(service.setPublishingEnabledByUser(!service.isPublishingEnabled()))
              setSaved(null)
            }}
          >
            <PowerIcon size={ICON_SIZES.xs} />
            {enabled ? 'ОТКЛЮЧИТЬ ОБРАБОТКУ' : 'ВКЛЮЧИТЬ ОБРАБОТКУ'}
          </Btn>
        }
        center={
          <>
            <InfoIcon size={ICON_SIZES.xs} color={COLORS.textSecondary} />
            <span>{saved || 'Голос не меняется без твоего решения. Всё обратимо.'}</span>
          </>
        }
        right={
          <>
            <Btn
              onClick={() => {
                const label = window.prompt('Название пресета:', 'Мой пресет')
                if (label) saveCustomPreset(label, settings)
              }}
            >
              <SaveIcon size={ICON_SIZES.xs} />
              СОХРАНИТЬ ПРЕСЕТ
            </Btn>
            <Btn variant='primary' onClick={handleSave}>
              СОХРАНИТЬ НАСТРОЙКИ
            </Btn>
          </>
        }
      />
    </ScreenShell>
  )
}

// ─── Собеседник в канале ─────────────────────────────────────────────────────

function PeerRow({ peer }) {
  const db = peer.level > 0 ? 20 * Math.log10(peer.level) : -100
  const verdict = verdictFor({ rmsDb: db, peakDb: db })
  const tone = verdict.tone

  return (
    <div
      css={css`
        display: flex;
        gap: ${space(1)};
        align-items: flex-start;
      `}
    >
      <span
        aria-hidden='true'
        css={css`
          flex: 0 0 auto;
          width: 40px;
          height: 40px;
          border-radius: ${RADII.round};
          border: 1px solid ${peer.speaking ? COLORS.cyan : COLORS.border};
          background: ${COLORS.bgElevated};
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          font-weight: 700;
          color: ${peer.speaking ? COLORS.cyan : COLORS.textSecondary};
          box-shadow: ${peer.speaking ? SHADOWS.glowCyan : 'none'};
          transition: box-shadow ${MOTION.hover};
        `}
      >
        {String(peer.name || '?').slice(0, 1).toUpperCase()}
      </span>

      <span
        css={css`
          flex: 1 1 auto;
          min-width: 0;
        `}
      >
        <span
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;
          `}
        >
          <span
            css={css`
              font-size: 0.78rem;
              font-weight: 600;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {peer.name}
          </span>
          {peer.isMe && <Chip tone='muted'>ТЫ</Chip>}
          {peer.isAI && <Chip tone='muted'>AI</Chip>}
          {peer.micEnabled ? (
            <MicIcon size={ICON_SIZES.xs} color={COLORS.textSecondary} />
          ) : (
            <MicOffIcon size={ICON_SIZES.xs} color={COLORS.error} />
          )}
        </span>

        <span
          css={css`
            display: flex;
            align-items: center;
            gap: ${space(0)};
            margin-top: 5px;
          `}
        >
          <span
            css={css`
              flex: 1 1 auto;
            `}
          >
            <LevelBar value={peer.level} tone={tone} />
          </span>
          <span
            css={css`
              font-size: 0.6rem;
              letter-spacing: 0.08em;
              color: ${TONE_COLOR[tone]};
              white-space: nowrap;
            `}
          >
            {verdict.label}
          </span>
        </span>

        {verdict.id !== 'good' && verdict.id !== 'silent' && (
          <span
            css={css`
              display: inline-block;
              margin-top: 5px;
            `}
          >
            <Chip tone={tone}>{verdict.peerHint}</Chip>
          </span>
        )}
      </span>
    </div>
  )
}

// ─── Шкала зон -60…0 ─────────────────────────────────────────────────────────

function ZoneMeter({ snapshot, verdict }) {
  const pos = dbToScale(snapshot?.rmsDb ?? -60) * 100
  const quietEnd = dbToScale(ZONES.quietMax) * 100
  const goodEnd = dbToScale(ZONES.goodMax) * 100
  const loudEnd = dbToScale(ZONES.loudMax) * 100

  return (
    <div>
      <div
        css={css`
          display: flex;
          font-size: 0.58rem;
          letter-spacing: 0.1em;
          color: ${COLORS.textSecondary};
          margin-bottom: 4px;
        `}
      >
        <span css={css`width: ${quietEnd}%;`}>СЛИШКОМ ТИХО</span>
        <span
          css={css`
            width: ${goodEnd - quietEnd}%;
            text-align: center;
            color: ${COLORS.success};
          `}
        >
          РАБОЧИЙ ДИАПАЗОН
        </span>
        <span
          css={css`
            width: ${loudEnd - goodEnd}%;
            text-align: center;
            color: ${COLORS.warning};
          `}
        >
          ГРОМКО
        </span>
        <span
          css={css`
            flex: 1 1 auto;
            text-align: right;
            color: ${COLORS.error};
          `}
        >
          ПЕРЕГРУЗ
        </span>
      </div>

      <div
        css={css`
          position: relative;
          height: 16px;
          border-radius: ${RADII.sm};
          border: 1px solid ${COLORS.border};
          overflow: hidden;
          background: linear-gradient(
            to right,
            ${COLORS.disabled} 0%,
            ${COLORS.disabled} ${quietEnd}%,
            ${COLORS.success} ${quietEnd}%,
            ${COLORS.success} ${goodEnd}%,
            ${COLORS.warning} ${goodEnd}%,
            ${COLORS.warning} ${loudEnd}%,
            ${COLORS.error} ${loudEnd}%
          );
        `}
      >
        <span
          aria-hidden='true'
          css={css`
            position: absolute;
            top: -2px;
            bottom: -2px;
            left: ${pos}%;
            width: 4px;
            background: ${COLORS.text};
            border-radius: ${RADII.xs};
            transition: left 0.08s linear;
          `}
        />
      </div>

      <div
        css={css`
          display: flex;
          justify-content: space-between;
          margin-top: 4px;
          font-size: 0.58rem;
          color: ${COLORS.textSecondary};
        `}
      >
        <span>-60</span>
        <span>-42</span>
        <span>-24</span>
        <span>-12</span>
        <span>-6</span>
        <span>0</span>
      </div>

      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: ${space(1)};
        `}
      >
        <span
          css={css`
            font-size: 0.8rem;
            font-weight: 700;
            letter-spacing: 0.1em;
            color: ${TONE_COLOR[verdict.tone]};
          `}
        >
          {verdict.ru}
        </span>
        <span
          css={css`
            font-size: ${TYPE.caption.size};
            color: ${COLORS.textSecondary};
          `}
        >
          удержание пика {(snapshot?.peakHoldAge ?? 0).toFixed(1)} с
        </span>
      </div>
    </div>
  )
}

// ─── График истории пиков ────────────────────────────────────────────────────

function HistoryChart({ history }) {
  const width = 100
  const height = 34
  const points = history.length
    ? history
        .map((point, index) => {
          const x = (index / Math.max(1, history.length - 1)) * width
          const y = height - dbToScale(point.peakDb) * height
          return `${x.toFixed(2)},${y.toFixed(2)}`
        })
        .join(' ')
    : ''

  return (
    <div
      css={css`
        margin-top: ${space(2)};
      `}
    >
      <div
        css={css`
          font-size: 0.6rem;
          letter-spacing: 0.14em;
          color: ${COLORS.textSecondary};
          margin-bottom: 4px;
        `}
      >
        ИСТОРИЯ ПИКОВ (60 СЕКУНД)
      </div>
      <div
        css={css`
          border: 1px solid ${COLORS.border};
          border-radius: ${RADII.md};
          background: ${COLORS.bgDeep};
          padding: ${space(0)};
        `}
      >
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio='none' css={css`display: block; width: 100%; height: 90px;`}>
          <line x1='0' y1={height - dbToScale(ZONES.goodMax) * height} x2={width} y2={height - dbToScale(ZONES.goodMax) * height} stroke={COLORS.warning} strokeWidth='0.3' strokeDasharray='2 2' opacity='0.6' />
          <line x1='0' y1={height - dbToScale(ZONES.quietMax) * height} x2={width} y2={height - dbToScale(ZONES.quietMax) * height} stroke={COLORS.disabled} strokeWidth='0.3' strokeDasharray='2 2' opacity='0.6' />
          {points && <polyline points={points} fill='none' stroke={COLORS.success} strokeWidth='0.6' vectorEffect='non-scaling-stroke' />}
        </svg>
      </div>
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          font-size: 0.58rem;
          color: ${COLORS.textSecondary};
          margin-top: 2px;
        `}
      >
        <span>60 с назад</span>
        <span>сейчас</span>
      </div>
    </div>
  )
}

// ─── Карточка предложения ────────────────────────────────────────────────────

function SuggestionCard({ suggestion, previewing, testLocally, onPreview, onApply, onDismiss }) {
  const [forever, setForever] = useState(false)
  const tone = suggestion.kind === 'quiet' ? COLORS.warning : COLORS.error

  return (
    <section
      css={css`
        border: 1px solid ${COLORS.borderStrong};
        border-radius: ${RADII.xl};
        background: ${COLORS.bgElevated};
        padding: ${space(2)};
        box-shadow: ${SHADOWS.raised};
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${space(1)};
          margin-bottom: ${space(1)};
        `}
      >
        <span
          css={css`
            font-size: 0.62rem;
            letter-spacing: 0.16em;
            color: ${tone};
          `}
        >
          ПРЕДЛОЖЕНИЕ
        </span>
        <button
          type='button'
          aria-label='Скрыть предложение'
          onClick={() => onDismiss(false)}
          css={css`
            margin-left: auto;
            appearance: none;
            background: transparent;
            border: none;
            color: ${COLORS.textSecondary};
            cursor: pointer;
            &:hover {
              color: ${COLORS.text};
            }
          `}
        >
          <XIcon size={ICON_SIZES.xs} />
        </button>
      </div>

      <div
        css={css`
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${space(2)};

          @media (max-width: 800px) {
            grid-template-columns: 1fr;
          }
        `}
      >
        <div>
          <div
            css={css`
              font-size: 1rem;
              font-weight: 700;
              margin-bottom: 4px;
            `}
          >
            {suggestion.ru}
          </div>
          <div
            css={css`
              font-size: ${TYPE.caption.size};
              color: ${COLORS.textSecondary};
              line-height: 1.5;
            `}
          >
            {suggestion.bodyRu}
          </div>

          <div
            css={css`
              margin-top: ${space(1)};
              border: 1px solid ${COLORS.border};
              border-radius: ${RADII.lg};
              overflow: hidden;
            `}
          >
            <div
              css={css`
                font-size: 0.6rem;
                letter-spacing: 0.14em;
                color: ${COLORS.textSecondary};
                padding: ${space(0)} ${space(1)};
                border-bottom: 1px solid ${COLORS.border};
              `}
            >
              ЧТО ИЗМЕНИТСЯ
            </div>
            {suggestion.changes.map(change => {
              const param = VOICE_PARAMS.find(p => p.id === change.id)
              return (
                <div
                  key={change.id}
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    gap: ${space(1)};
                    padding: ${space(0)} ${space(1)};
                    font-size: 0.74rem;
                  `}
                >
                  <span>{param?.ru || change.label}</span>
                  <span
                    css={css`
                      color: ${COLORS.cyan};
                      font-variant-numeric: tabular-nums;
                    `}
                  >
                    {change.display} → {param ? formatParamValue(param, change.value) : change.value}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <div>
          <div
            css={css`
              border: 1px solid ${COLORS.border};
              border-radius: ${RADII.lg};
              padding: ${space(1)};
            `}
          >
            <div
              css={css`
                font-size: 0.6rem;
                letter-spacing: 0.14em;
                color: ${COLORS.textSecondary};
                margin-bottom: 6px;
              `}
            >
              ЧЕМ ЭТО ПОМОЖЕТ
            </div>
            {suggestion.why.map(item => (
              <div
                key={item}
                css={css`
                  display: flex;
                  gap: 6px;
                  align-items: flex-start;
                  font-size: ${TYPE.caption.size};
                  color: ${COLORS.textSecondary};
                  margin-bottom: 3px;
                `}
              >
                <CheckIcon size={12} color={COLORS.success} />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div
            css={css`
              display: flex;
              gap: ${space(0)};
              align-items: center;
              margin-top: ${space(1)};
              padding: ${space(0)} ${space(1)};
              border: 1px solid ${COLORS.successSoft};
              border-radius: ${RADII.lg};
              background: ${COLORS.successSoft};
              font-size: ${TYPE.caption.size};
              color: ${COLORS.textSecondary};
            `}
          >
            <HeartIcon size={ICON_SIZES.xs} color={COLORS.success} />
            <span>Только лёгкая обработка. Голос не подменяем.</span>
          </div>
        </div>
      </div>

      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${space(1)};
          margin-top: ${space(2)};
          flex-wrap: wrap;
        `}
      >
        {testLocally && (
          <Btn variant={previewing ? 'success' : 'accent'} onClick={onPreview}>
            <PlayIcon size={ICON_SIZES.xs} />
            {previewing ? 'ОСТАНОВИТЬ ПРОБУ' : 'ПОСЛУШАТЬ СЕБЯ'}
          </Btn>
        )}
        <Btn variant='primary' onClick={onApply}>
          ПРИМЕНИТЬ
        </Btn>
        <Btn onClick={() => onDismiss(forever)}>НЕ СЕЙЧАС</Btn>
        <label
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: ${TYPE.caption.size};
            color: ${COLORS.textSecondary};
            cursor: pointer;
          `}
        >
          <input type='checkbox' checked={forever} onChange={event => setForever(event.target.checked)} />
          Больше не спрашивать
        </label>
        {previewing && (
          <span
            css={css`
              font-size: ${TYPE.caption.size};
              color: ${COLORS.cyan};
            `}
          >
            Слышишь только ты — наружу проба не идёт.
          </span>
        )}
      </div>
    </section>
  )
}

// ─── Мелочи ──────────────────────────────────────────────────────────────────

function EmptyNote({ text }) {
  return (
    <div
      css={css`
        padding: ${space(2)};
        text-align: center;
        font-size: ${TYPE.caption.size};
        color: ${COLORS.textSecondary};
      `}
    >
      {text}
    </div>
  )
}

function fmtDb(db) {
  if (!Number.isFinite(db) || db <= -99) return '—'
  return `${db.toFixed(1)} dB`
}
